import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, CreditCard, ShoppingBag, UtensilsCrossed, Ticket, Calendar } from 'lucide-react';
import { useCartStore } from '../../store/cart';
import { formatPrice } from '../../lib/utils';

export function CartPage() {
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    subtotal,
    merchandiseItems,
    foodItems,
    ticketItems,
  } = useCartStore();

  const merchandiseList = merchandiseItems();
  const foodList = foodItems();
  const ticketList = ticketItems();
  const total = subtotal();

  if (items.length === 0) {
    return (
      <div className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingCart size={48} className="text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h1>
            <p className="text-gray-600 mb-8">
              Add some event tickets, merchandise, or food items to get started.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/tickets"
                className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
              >
                <Ticket size={20} className="mr-2" />
                Browse Events
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
              >
                <ShoppingBag size={20} className="mr-2" />
                Browse Merchandise
              </Link>
              <Link
                to="/food"
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-700 text-white font-medium rounded-xl hover:bg-gray-600 transition-colors"
              >
                <UtensilsCrossed size={20} className="mr-2" />
                Order Food
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Your Cart</h1>
            <p className="text-gray-600">{totalItems()} item{totalItems() !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={clearCart}
            className="text-sm text-gray-500 hover:text-red-600 transition-colors"
          >
            Clear cart
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tickets Section */}
            {ticketList.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Ticket size={20} className="text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Event Tickets</h2>
                </div>
                <div className="space-y-4">
                  {ticketList.map((item) => (
                    <TicketCartItemCard
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onRemove={removeItem}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Merchandise Section */}
            {merchandiseList.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag size={20} className="text-red-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Merchandise</h2>
                </div>
                <div className="space-y-4">
                  {merchandiseList.map((item) => (
                    <CartItemCard
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onRemove={removeItem}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Food Section */}
            {foodList.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <UtensilsCrossed size={20} className="text-orange-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Food & Drinks</h2>
                </div>
                <div className="space-y-4">
                  {foodList.map((item) => (
                    <CartItemCard
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onRemove={removeItem}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatPrice(total)}</span>
                </div>
                {ticketList.length > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Event Tickets ({ticketList.reduce((sum, i) => sum + i.quantity, 0)})</span>
                    <span>
                      {formatPrice(
                        ticketList.reduce((sum, i) => sum + i.price * i.quantity, 0)
                      )}
                    </span>
                  </div>
                )}
                {merchandiseList.length > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Merchandise ({merchandiseList.length})</span>
                    <span>
                      {formatPrice(
                        merchandiseList.reduce((sum, i) => sum + i.price * i.quantity, 0)
                      )}
                    </span>
                  </div>
                )}
                {foodList.length > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Food & Drinks ({foodList.length})</span>
                    <span>
                      {formatPrice(
                        foodList.reduce((sum, i) => sum + i.price * i.quantity, 0)
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full flex items-center justify-center px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
              >
                <CreditCard size={20} className="mr-2" />
                Checkout
              </button>

              <div className="mt-4 flex gap-2">
                <Link
                  to="/tickets"
                  className="flex-1 text-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  + Tickets
                </Link>
                <Link
                  to="/shop"
                  className="flex-1 text-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  + Shop
                </Link>
                <Link
                  to="/food"
                  className="flex-1 text-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  + Food
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Continue Shopping */}
        <div className="mt-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
}

interface CartItemCardProps {
  item: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    size?: string;
    color?: string;
    imageUrl?: string;
    notes?: string;
  };
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}

function CartItemCard({ item, onUpdateQuantity, onRemove }: CartItemCardProps) {
  return (
    <div className="card p-4 flex gap-4">
      {/* Image */}
      <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={24} className="text-gray-400" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
        <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500">
          {item.size && <span>Size: {item.size}</span>}
          {item.color && <span>Color: {item.color}</span>}
        </div>
        {item.notes && (
          <p className="text-sm text-gray-500 mt-1 truncate">Note: {item.notes}</p>
        )}
        <p className="font-semibold text-gray-900 mt-2">{formatPrice(item.price)}</p>
      </div>

      {/* Quantity Controls */}
      <div className="flex flex-col items-end justify-between">
        <button
          onClick={() => onRemove(item.id)}
          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
        >
          <Trash2 size={18} />
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
          >
            <Minus size={14} />
          </button>
          <span className="w-8 text-center font-medium">{item.quantity}</span>
          <button
            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Ticket-specific cart item card
interface TicketCartItemCardProps {
  item: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    imageUrl?: string;
    eventDate?: string;
    ticketType?: string;
  };
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}

function TicketCartItemCard({ item, onUpdateQuantity, onRemove }: TicketCartItemCardProps) {
  return (
    <div className="card p-4 flex gap-4 border-l-4 border-purple-500">
      {/* Image */}
      <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-purple-100">
            <Ticket size={24} className="text-purple-500" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
        <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500">
          {item.eventDate && (
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(item.eventDate).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          )}
          {item.ticketType && <span>{item.ticketType}</span>}
        </div>
        <p className="font-semibold text-gray-900 mt-2">{formatPrice(item.price)} each</p>
      </div>

      {/* Quantity Controls */}
      <div className="flex flex-col items-end justify-between">
        <button
          onClick={() => onRemove(item.id)}
          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
        >
          <Trash2 size={18} />
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
          >
            <Minus size={14} />
          </button>
          <span className="w-8 text-center font-medium">{item.quantity}</span>
          <button
            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
